import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { Checkbox, Text } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { centsToDollarsDisplay } from 'utils/currency';

interface IRowProps {
  $columns: number;
}

const Row = styled.div<IRowProps>`
  display: grid;
  grid-template-columns: 1fr;
  margin-bottom: ${toRem(theme.space.lg)};
  gap: ${toRem(theme.space.sm)};
  align-items: center;
  width: 100%;
  border-radius: ${toRem(8)};
  border: 1px solid ${theme.color.neutral[200]};
  padding: ${toRem(theme.space.lg)};
  background-color: ${theme.color.neutral[50]};

  /* All pills on one row when wide, then 3, 2 and 1 per row as the screen narrows.
     At 2 per row they fill top to bottom, so the first half (paid, payroll, cash) stays together in the left column. */
  @media (min-width: ${theme.breakpoint.xsmall}px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: ${({ $columns }) => `repeat(${Math.ceil($columns / 2)}, auto)`};
    grid-auto-flow: column;
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: none;
    grid-auto-flow: row;
  }

  @media (min-width: ${theme.breakpoint.xlarge}px) {
    grid-template-columns: ${({ $columns }) => `repeat(${$columns}, 1fr)`};
  }

  input {
    margin-right: ${toRem(theme.space.sm)};
  }
`;

interface IPillProps {
  appearance?: Appearance;
}

const Pill = styled.div<IPillProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: ${toRem(30)};
  padding: ${toRem(theme.space.sm)} ${toRem(theme.space.md)};
  background-color: ${theme.color.white};
  text-transform: capitalize;
  gap: ${toRem(theme.space.xs)};

  label {
    font-weight: ${theme.font.weight.semiBold};
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};
  }

  &:hover {
    border: ${toRem(1)} solid ${theme.color.neutral[400]};
  }

  .react-loading-skeleton {
    opacity: 0.3;
    border-radius: ${toRem(8)};
  }

  ${({ appearance }) =>
    appearance === Appearance.SUCCESS &&
    css`
      background-color: ${theme.color.success[25]};
      border: ${toRem(1)} solid ${theme.color.success[50]};
      label,
      p {
        color: ${theme.color.success[100]};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.success[50]};
        background-image: ${`linear-gradient(90deg, ${theme.color.success[50]}, ${theme.color.neutral[25]}, ${theme.color.success[50]})`};
      }
    `}

  ${({ appearance }) =>
    appearance === Appearance.SECONDARY &&
    css`
      background-color: ${theme.color.secondary[25]};
      border: ${toRem(1)} solid ${theme.color.secondary[400]};
      label,
      p {
        color: ${theme.color.secondary[700]};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.secondary[100]};
        background-image: ${`linear-gradient(90deg, ${theme.color.secondary[100]}, ${theme.color.neutral[25]}, ${theme.color.secondary[100]})`};
      }
    `}

    ${({ appearance }) =>
    appearance === Appearance.WARNING &&
    css`
      background-color: ${theme.color.warning[25]};
      border: ${toRem(1)} solid ${theme.color.warning[50]};
      label,
      p {
        color: ${theme.color.warning[100]};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.warning[50]};
        background-image: ${`linear-gradient(90deg, ${theme.color.warning[50]}, ${theme.color.neutral[25]}, ${theme.color.warning[50]})`};
      }
    `}

    ${({ appearance }) =>
    appearance === Appearance.DANGER &&
    css`
      background-color: ${theme.color.danger[25]};
      border: ${toRem(1)} solid ${theme.color.danger[50]};
      label,
      p {
        color: ${theme.color.danger[100]};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.danger[50]};
        background-image: ${`linear-gradient(90deg, ${theme.color.danger[50]}, ${theme.color.neutral[25]}, ${theme.color.danger[50]})`};
      }
    `}

    ${({ appearance }) =>
    appearance === Appearance.NEUTRAL &&
    css`
      background-color: ${theme.color.neutral[100]};
      border: ${toRem(1)} solid ${theme.color.neutral[500]};
      label,
      p {
        color: ${theme.color.neutral[900]};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.neutral[300]};
        background-image: ${`linear-gradient(90deg, ${theme.color.neutral[300]}, ${theme.color.neutral[25]}, ${theme.color.neutral[300]})`};
      }
    `}

    ${({ appearance }) =>
    appearance === Appearance.CASH &&
    css`
      background-color: ${theme.color.cash[25]};
      border: ${toRem(1)} solid ${theme.color.cash[50]};
      label,
      p {
        color: ${theme.color.neutral[600]};
      }
      .react-loading-skeleton {
        background-color: ${theme.color.cash[50]};
        background-image: ${`linear-gradient(90deg, ${theme.color.cash[50]}, ${theme.color.neutral[25]}, ${theme.color.cash[50]})`};
      }
    `}
`;

interface IStatus {
  label: string;
  value: number;
  count: number;
  appearance?: Appearance;
}

interface IStatusPillsProps {
  statuses: IStatus[];
  activeStatuses?: string[];
  onChange?(selectedStatus: string[]): void;
  isLoading?: boolean;
}

export const StatusPills = ({ statuses, activeStatuses, onChange, isLoading }: IStatusPillsProps) => {
  const onToggleCheckbox = (value: string, checked: boolean) => {
    let status;
    if (checked === true) {
      status = [...activeStatuses, value];
    } else {
      status = activeStatuses.filter((val) => val !== value);
    }
    onChange(status);
  };

  const getAppearance = (status: IStatus) => (!activeStatuses || activeStatuses?.includes(status.label) ? status.appearance : undefined);

  return (
    <Row $columns={statuses.length} data-cy="status-pills">
      {statuses.map((status) => {
        const label = `${status.label}${isLoading ? '' : ` (${status.count})`}`;
        return (
          <Pill appearance={getAppearance(status)} key={status.label} data-cy={`status-pill-${status.label}`}>
            {activeStatuses ? (
              <Checkbox
                id={`status-checkbox-${status.label}`}
                name={`status-checkbox-${status.label}`}
                data-cy={`status-checkbox-${status.label}`}
                label={label}
                size="small"
                disabled={isLoading}
                checked={activeStatuses.includes(status.label)}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  onToggleCheckbox(status.label, e.target.checked);
                }}
              />
            ) : (
              <Text size="xxsmall" fontWeight="semibold">
                {label}
              </Text>
            )}
            {isLoading ? (
              <Skeleton width={50} />
            ) : (
              <Text size="xxsmall" fontWeight="semibold">
                {centsToDollarsDisplay(status.value)}
              </Text>
            )}
          </Pill>
        );
      })}
    </Row>
  );
};
